/**
 * Relationship Type Selector Component
 * Beautiful UI for selecting relationship types with tier-based access control
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ChevronRight, Sparkles } from 'lucide-react';
import {
  RELATIONSHIP_TYPES,
  getRelationshipTypesByCategory,
  getRequiredTierForRelationshipType
} from '../../config/subscriptionTiers';
import { useSubscription } from '../../contexts/SubscriptionContext';

const RelationshipTypeSelector = ({ value, onChange, showLabel = true }) => {
  const { canUseRelationshipType, subscriptionTier, promptUpgrade } = useSubscription();
  const [selectedCategory, setSelectedCategory] = useState('Household');

  const categorizedTypes = getRelationshipTypesByCategory();
  const categories = Object.keys(categorizedTypes);

  const handleSelect = (relationshipType) => {
    const isAvailable = canUseRelationshipType(relationshipType.id);

    if (!isAvailable) {
      promptUpgrade({
        type: 'relationshipType',
        relationshipTypeId: relationshipType.id,
        requiredTier: getRequiredTierForRelationshipType(relationshipType.id)
      });
      return;
    }

    onChange(relationshipType.id);
  };

  return (
    <div className="space-y-4">
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Relationship Type
        </label>
      )}

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
              ${selectedCategory === category
                ? 'bg-gradient-honey text-white shadow-glow'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }
            `}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Relationship Type Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {categorizedTypes[selectedCategory]?.map((type, index) => {
          const isAvailable = canUseRelationshipType(type.id);
          const isSelected = value === type.id;
          const requiredTier = getRequiredTierForRelationshipType(type.id);

          return (
            <motion.button
              key={type.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => handleSelect(type)}
              className={`
                relative p-4 rounded-xl text-left transition-all
                ${isSelected
                  ? 'bg-gradient-honey text-white shadow-glow ring-2 ring-honey-500 ring-offset-2'
                  : isAvailable
                  ? 'glass-card hover:shadow-lg'
                  : 'glass-card opacity-60 cursor-not-allowed'
                }
              `}
            >
              {/* Premium Badge */}
              {!isAvailable && (
                <div className="absolute top-2 right-2">
                  <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-white text-xs font-bold shadow-lg">
                    <Lock className="w-3 h-3" />
                    {requiredTier}
                  </div>
                </div>
              )}

              {/* Selected Badge */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <div className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur rounded-full text-xs font-bold">
                    <Sparkles className="w-3 h-3" />
                    Selected
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="text-3xl flex-shrink-0">{type.icon}</div>
                <div className="flex-1 min-w-0">
                  <h4 className={`
                    font-semibold mb-1 text-sm
                    ${isSelected ? 'text-white' : 'text-gray-900 dark:text-white'}
                  `}>
                    {type.name}
                  </h4>
                  <p className={`
                    text-xs line-clamp-2
                    ${isSelected ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}
                  `}>
                    {type.description}
                  </p>
                </div>
                {isAvailable && (
                  <ChevronRight className={`
                    w-5 h-5 flex-shrink-0
                    ${isSelected ? 'text-white' : 'text-gray-400'}
                  `} />
                )}
              </div>

              {/* Upgrade Hint */}
              {!isAvailable && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Upgrade to {requiredTier} to unlock
                  </p>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Info Note */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-xs text-blue-700 dark:text-blue-400">
          💡 Your current plan ({subscriptionTier}) includes{' '}
          {subscriptionTier === 'Free' ? '1 relationship type' :
           subscriptionTier === 'Premium' ? '5 relationship types' :
           'all relationship types'}
        </p>
      </div>
    </div>
  );
};

export default RelationshipTypeSelector;
